import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Franja } from '../componentes/Franja';
import { Ayuda } from '../componentes/Ayuda';
import { Boton } from '../componentes/Boton';
import { Icono } from '../componentes/Icono';
import { barraInferior } from '../logica/navegacion';
import { dibujoDe } from './iconosMenu';
import type { Vista } from './PanelNegocio';
import { inicialesDe, lineaDe } from './negocio';
import { PersonalizarBarra } from './Personalizar';
import { Avatar, EtiquetaEstado, TarjetaAviso } from './piezas';
import { Privacidad } from './Privacidad';
import logo from '../estilo/comun/assets/wordmark-cropped.png';

/**
 * Marco de móvil (< 1024; componentes.md §1 «Móvil», §1g): scrollea la página entera; barra de arriba compacta y pegada
 * (negocio en rojo que se cruza con el nombre de la sección, «campana» y avatar) que se esconde al bajar; título con el
 * contenido; barra inferior fija con las entradas de `mobile_bar` (las que eligió la persona o las 4 primeras de
 * Operativa; G97, G129) y «Más», que crece hacia arriba con el negocio («Cambiar»), el resto del menú por categorías en
 * baldosas (G128) y «Personalizar menú», que abre la hoja «Tu barra y «Más»» (§1g).
 */
type Pop = 'campana' | 'cuenta' | null;

export function MarcoMovil({ v }: { v: Vista }) {
    const [pop, setPop] = useState<Pop>(null);
    const [mas, setMas] = useState(false);
    const [vistaNegocios, setVistaNegocios] = useState(false);
    const [oculto, setOculto] = useState(false);
    const [paso, setPaso] = useState(false);
    const [personalizando, setPersonalizando] = useState(false);
    const titulo = useRef<HTMLDivElement>(null);
    const masContenido = useRef<HTMLDivElement>(null);
    const negociosContenido = useRef<HTMLDivElement>(null);
    const [altoMas, setAltoMas] = useState(0);

    // Al bajar más de 12 px seguidos (pasados 60) se esconden las dos barras; al subir 12 px, vuelven.
    useEffect(() => {
        let ultimo = window.scrollY;
        let acumulado = 0;
        const mira = () => {
            const y = window.scrollY;
            const d = y - ultimo;
            ultimo = y;
            acumulado = Math.sign(d) === Math.sign(acumulado) ? acumulado + d : d;
            if (y < 60) {
                setOculto(false);
            } else if (acumulado > 12) {
                setOculto(true);
            } else if (acumulado < -12) {
                setOculto(false);
            }
            const t = titulo.current?.getBoundingClientRect();
            setPaso(t !== undefined && t.bottom < 52);
        };
        window.addEventListener('scroll', mira, { passive: true });
        return () => {
            window.removeEventListener('scroll', mira);
        };
    }, []);

    useLayoutEffect(() => {
        const el = vistaNegocios ? negociosContenido.current : masContenido.current;
        setAltoMas(el === null ? 0 : el.scrollHeight);
    }, [mas, vistaNegocios, v.menu, v.membresias]);

    useEffect(() => {
        if (!personalizando) {
            return;
        }
        const esc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setPersonalizando(false);
            }
        };
        window.addEventListener('keydown', esc);
        return () => {
            window.removeEventListener('keydown', esc);
        };
    }, [personalizando]);

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [v.actual?.clave]);

    // Marco de portal (§17): todas sus entradas en la barra de abajo, sin «Más» (el portal tiene cinco y ningún negocio).
    const portal = v.portal;
    const barra = portal === undefined ? barraInferior(v.menu) : { barra: v.menu.entradas, mas: [] };
    const actual = v.actual?.clave ?? null;
    const enBarra = barra.barra.findIndex((e) => e.clave === actual);
    const enMas = barra.mas.some((g) => g.entradas.some((e) => e.clave === actual));
    const columnas = barra.barra.length + (portal === undefined ? 1 : 0);
    const indice = mas ? barra.barra.length : enBarra >= 0 ? enBarra : enMas ? barra.barra.length : -1;
    const variosNegocios = v.membresias.length > 1;
    const verBarras = !oculto || mas || pop !== null;
    const altoMaximo = typeof window === 'undefined' ? 700 : window.innerHeight - 24;

    const cerrarTodo = () => {
        setPop(null);
        setMas(false);
        setVistaNegocios(false);
    };
    const entra = (on: boolean, i: number) => ({ transform: on ? 'none' : 'translateY(-16px)', opacity: on ? 1 : 0, transitionDelay: `${String(on ? i * 60 : 0)}ms` });

    return (
        <div className="rv-marco-m">
            <header className={`rv-barra-arriba${portal !== undefined ? ' rv-barra-arriba--portal' : ''}${paso && verBarras ? ' rv-barra-arriba--linea' : ''}${verBarras ? '' : ' rv-barra-arriba--oculta'}${pop !== null ? ' rv-barra-arriba--encima' : ''}`}>
                {portal !== undefined ? (
                    <div className="rv-cab-portal">
                        <img src={logo} alt="Reservaria" />
                        <span>
                            <Icono nombre={portal.icono} />
                            {portal.ceja}
                        </span>
                    </div>
                ) : (
                    <div className="rv-cab-mov">
                        <span className="rv-cab-mov__negocio" aria-hidden={paso} style={{ opacity: paso ? 0 : 1, transform: paso ? 'translateY(-6px)' : 'none' }}>
                            {v.nombreNegocio}
                        </span>
                        <span className="rv-cab-mov__seccion" aria-hidden={!paso} style={{ opacity: paso ? 1 : 0, transform: paso ? 'none' : 'translateY(6px)' }}>
                            {v.titulo}
                        </span>
                    </div>
                )}
                <div className="rv-barra-arriba__herr">
                    <Ayuda tarjetas={v.herramientas.ayuda} />
                    {portal === undefined && (
                        <button
                            type="button"
                            className="rv-bi rv-campana-b rv-campana-m"
                            aria-label={v.campana.sinLeer > 0 ? `Avisos: ${String(v.campana.sinLeer)} sin leer` : 'Avisos'}
                            aria-expanded={pop === 'campana'}
                            style={{ background: pop === 'campana' ? 'var(--rv-ink)' : '#fff', color: pop === 'campana' ? '#fff' : 'var(--rv-ink)' }}
                            onClick={() => {
                                setMas(false);
                                setPop((p) => (p === 'campana' ? null : 'campana'));
                                v.campana.abrir();
                            }}
                        >
                            <Icono nombre="bell" />
                            {v.campana.sinLeer > 0 && (
                                <span className="rv-campana-b__n" style={{ opacity: pop === 'campana' ? 0 : 1, transform: pop === 'campana' ? 'scale(0.6)' : 'scale(1)' }}>
                                    {v.campana.sinLeer}
                                </span>
                            )}
                        </button>
                    )}
                    <button
                        type="button"
                        className="rv-avatar-m"
                        aria-label="Tu cuenta"
                        aria-expanded={pop === 'cuenta'}
                        style={{ boxShadow: `0 0 0 2px ${pop === 'cuenta' ? 'var(--rv-red)' : 'transparent'}` }}
                        onClick={() => {
                            setMas(false);
                            setPop((p) => (p === 'cuenta' ? null : 'cuenta'));
                        }}
                    >
                        {v.persona.iniciales}
                    </button>
                </div>
            </header>

            <div ref={titulo} className="rv-titulo-m">
                <h1>{v.titulo}</h1>
            </div>
            {v.soloLectura && v.franja === null && (
                <div className="rv-avisos-fijos-m" style={{ paddingBottom: 12 }}>
                    <span className="rv-chip-sl rv-chip-sl--claro" style={{ display: 'inline-flex', minHeight: 32 }}>
                        <Icono nombre="lock" />
                        <span>Solo lectura</span>
                    </span>
                </div>
            )}
            {v.franja !== null && (
                <div className="rv-avisos-fijos-m">
                    <Franja
                        key={v.franja.tipo}
                        tipo={v.franja.tipo}
                        titulo={v.franja.titulo}
                        tituloCorto={v.franja.tituloCorto}
                        texto={v.franja.texto}
                        accion={v.franja.accion}
                        movil
                        cerrable={v.franja.cerrable}
                        etiquetaCerrar={`Cerrar el aviso «${v.franja.tituloCorto}». Seguirá en la campana`}
                        espacio={12}
                        onAccion={() => {
                            if (v.franja !== null) {
                                v.accionFranja(v.franja);
                            }
                        }}
                        onCerrar={() => {
                            if (v.franja !== null) {
                                v.cerrarFranja(v.franja);
                            }
                        }}
                    />
                </div>
            )}
            <div className="rv-contenido-m">
                <div className="rv-contenido__columna">
                    {v.herramientas.acciones.length > 0 && (
                        <div className="rv-acciones-m">
                            {v.herramientas.acciones.map((a) => (
                                <Boton key={a.clave} movil tipo={a.secundaria === true ? 'secundario' : 'principal'} icono={a.icono} disabled={a.desactivado} onClick={a.onClick}>
                                    {a.texto}
                                </Boton>
                            ))}
                        </div>
                    )}
                    {v.contenido}
                    <div className="rv-final-m" />
                </div>
            </div>

            {/* Campana y tu cuenta: tarjetas que caen bajo la barra de arriba */}
            <div className="rv-velo-claro" aria-hidden="true" style={{ opacity: pop === null ? 0 : 1, pointerEvents: pop === null ? 'none' : 'auto' }} onClick={cerrarTodo} />
            <div role="dialog" aria-label="Avisos" aria-hidden={pop !== 'campana'} className="rv-pop-m" style={{ pointerEvents: pop === 'campana' ? 'auto' : 'none', visibility: pop === 'campana' ? 'visible' : 'hidden' }}>
                <div className="rv-pop-m__cab" style={{ opacity: pop === 'campana' ? 1 : 0 }}>
                    <span className="rv-pop-m__ceja">{`${String(v.campana.elementos.length)} avisos`}</span>
                    <button
                        type="button"
                        className="rv-pop-m__leidos"
                        onClick={() => {
                            v.campana.leerTodos();
                            setPop(null);
                        }}
                    >
                        Marcar como leídos
                    </button>
                </div>
                <div className="rv-pop-m__lista rv-noscroll">
                            {v.campana.elementos.length === 0 && <p className="rv-campana-vacia">{v.campana.vacio}</p>}
                    {v.campana.elementos.map((e, i) => (
                        <TarjetaAviso
                            key={e.clave}
                            e={e}
                            sombra
                            style={{ ...entra(pop === 'campana', i), flexShrink: 0 }}
                            onClick={() => {
                                v.campana.tocar(e);
                            }}
                            onAccion={() => {
                                setPop(null);
                                v.campana.accion(e);
                            }}
                        />
                    ))}
                </div>
                {v.campana.verTodos !== null && (
                    <button type="button" className="rv-pop-m__todos" onClick={v.campana.verTodos}>
                        <Icono nombre="bell-ring" />
                        <span>Ver todos los avisos</span>
                    </button>
                )}
            </div>
            <div role="dialog" aria-label="Tu cuenta" aria-hidden={pop !== 'cuenta'} className="rv-pop-m" style={{ pointerEvents: pop === 'cuenta' ? 'auto' : 'none', visibility: pop === 'cuenta' ? 'visible' : 'hidden' }}>
                <div className="rv-cuenta-m" style={entra(pop === 'cuenta', 0)}>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>{v.persona.nombre}</span>
                    <span className="rv-cuenta-m__email">{v.persona.email}</span>
                    <span className="rv-cuenta-m__rol">{v.persona.rol}</span>
                </div>
                <div className="rv-cuenta-m-lista" style={entra(pop === 'cuenta', 1)}>
                    {v.menu.perfil.map((e) => (
                        <button
                            key={e.clave}
                            type="button"
                            className="rv-cuenta-op"
                            onClick={() => {
                                setPop(null);
                                v.ir(e);
                            }}
                        >
                            <Icono nombre={dibujoDe(e.icono)} />
                            <span style={{ flex: 1 }}>{e.etiqueta}</span>
                        </button>
                    ))}
                    <button type="button" className="rv-cuenta-op rv-cuenta-op--salir" onClick={v.cerrarSesion}>
                        <Icono nombre="log-out" />
                        <span style={{ flex: 1 }}>Cerrar sesión</span>
                    </button>
                </div>
            </div>

            {/* Barra inferior y «Más» */}
            <div className="rv-velo-mas" aria-hidden="true" style={{ opacity: mas ? 1 : 0, pointerEvents: mas ? 'auto' : 'none' }} onClick={cerrarTodo} />
            <nav aria-label={portal === undefined ? 'Menú del panel' : 'Menú del portal'} className={`rv-barra-abajo${verBarras ? '' : ' rv-barra-abajo--oculta'}`} style={{ height: mas ? Math.min(altoMaximo, 72 + altoMas) : 72 }}>
                <div className="rv-barra-abajo__mas" style={{ opacity: mas ? 1 : 0, transform: mas ? 'none' : 'translateY(16px)' }} aria-hidden={!mas}>
                    <div className="rv-barra-abajo__vistas" style={{ transform: vistaNegocios ? 'translateX(-50%)' : 'none' }}>
                        <div ref={masContenido} className="rv-barra-abajo__vista rv-noscroll" style={{ opacity: vistaNegocios ? 0 : 1 }}>
                            <div className="rv-mas__cab">
                                <span className="rv-mas__ceja">Más</span>
                                <button type="button" className="rv-mas__x" aria-label="Cerrar" onClick={cerrarTodo}>
                                    <Icono nombre="x" />
                                </button>
                            </div>
                            <button
                                type="button"
                                className="rv-mas__negocio"
                                aria-disabled={!variosNegocios}
                                onClick={() => {
                                    if (variosNegocios) {
                                        setVistaNegocios(true);
                                    }
                                }}
                            >
                                <Avatar texto={v.inicialesNegocio} tamano={38} fondo="var(--rv-red)" tinta="#fff" />
                                <span className="rv-mas__negocio-textos">
                                    <span className="rv-mas__negocio-nombre">{v.nombreNegocio}</span>
                                    <span className="rv-mas__negocio-linea">{v.lineaNegocio}</span>
                                </span>
                                {variosNegocios ? (
                                    <span className="rv-mas__cambiar">
                                        Cambiar
                                        <Icono nombre="chevron-right" />
                                    </span>
                                ) : (
                                    (() => {
                                        const m = v.membresias[0];
                                        return m === undefined ? null : <EtiquetaEstado estado={m.subscription_status} />;
                                    })()
                                )}
                            </button>
                            {barra.mas.map((g) => (
                                <div key={g.clave} role="group" aria-label={g.titulo} className="rv-mas__grupo">
                                    <div aria-hidden="true" className="rv-mas__grupo-ceja">
                                        {g.titulo}
                                    </div>
                                    <div className="rv-mas__rejilla">
                                        {g.entradas.map((e) => (
                                            <button
                                                key={e.clave}
                                                type="button"
                                                className="rv-mas__baldosa"
                                                aria-current={e.clave === actual && !e.noContratada ? 'page' : undefined}
                                                onClick={() => {
                                                    cerrarTodo();
                                                    v.ir(e);
                                                }}
                                            >
                                                <Icono nombre={dibujoDe(e.icono)} />
                                                <span>{e.etiqueta}</span>
                                                {e.noContratada && <i className="icon-plus rv-mas__mas" aria-label="No contratado" />}
                                                {e.contador !== null && e.contador > 0 && <span className="rv-cifra">{e.contador}</span>}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            {v.personalizar !== null && (
                                <button
                                    type="button"
                                    className="rv-mas__personalizar"
                                    onClick={() => {
                                        setPersonalizando(true);
                                    }}
                                >
                                    <Icono nombre="sliders-horizontal" />
                                    <span>Personalizar menú</span>
                                </button>
                            )}
                        </div>
                        <div ref={negociosContenido} className="rv-barra-abajo__vista rv-noscroll" style={{ opacity: vistaNegocios ? 1 : 0, gap: 8 }}>
                            <div className="rv-mas__cab" style={{ paddingBottom: 4 }}>
                                <button
                                    type="button"
                                    className="rv-mas__atras"
                                    onClick={() => {
                                        setVistaNegocios(false);
                                    }}
                                >
                                    <Icono nombre="chevron-left" />
                                    <span>Atrás</span>
                                </button>
                                <span className="rv-mas__ceja">Tus negocios</span>
                            </div>
                            {v.membresias.map((m) => {
                                const es = m.business_id === v.negocioId;
                                return (
                                    <button
                                        key={m.business_id}
                                        type="button"
                                        className="rv-mas__fila-negocio"
                                        aria-current={es}
                                        onClick={() => {
                                            cerrarTodo();
                                            v.cambiarNegocio(m.business_id);
                                        }}
                                    >
                                        <Avatar texto={inicialesDe(m)} tamano={38} fondo={es ? 'var(--rv-red)' : '#fff'} tinta={es ? '#fff' : 'var(--rv-ink)'} />
                                        <span className="rv-mas__negocio-textos" style={{ gap: 3 }}>
                                            <span className="rv-mas__negocio-nombre">{m.business_name}</span>
                                            <span className="rv-mas__negocio-linea">{lineaDe(m)}</span>
                                        </span>
                                        <EtiquetaEstado estado={m.subscription_status} />
                                        {es && <Icono nombre="check" tamano={18} />}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className="rv-pestanas">
                    <div className="rv-pestanas__rejilla" style={{ gridTemplateColumns: `repeat(${String(columnas)}, minmax(0, 1fr))` }}>
                        <span className="rv-pestanas__pildora" style={{ width: `${String(100 / columnas)}%`, transform: `translateX(${String(Math.max(0, indice) * 100)}%)`, opacity: indice < 0 ? 0 : 1 }} />
                        {barra.barra.map((e) => (
                            <button
                                key={e.clave}
                                type="button"
                                className="rv-pestana"
                                aria-current={e.clave === actual && !mas ? 'page' : undefined}
                                onClick={() => {
                                    cerrarTodo();
                                    if (e.clave === actual) {
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    } else {
                                        v.ir(e);
                                    }
                                }}
                            >
                                <Icono nombre={dibujoDe(e.icono)} />
                                <span>{e.etiqueta}</span>
                            </button>
                        ))}
                        {portal === undefined && (
                            <button
                                type="button"
                                className="rv-pestana"
                                aria-expanded={mas}
                                onClick={() => {
                                    setPop(null);
                                    setVistaNegocios(false);
                                    setMas((m) => !m);
                                }}
                            >
                                <Icono nombre="ellipsis" />
                                <span>Más</span>
                            </button>
                        )}
                    </div>
                </div>
            </nav>
            {v.personalizar !== null && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Personalizar menú"
                    aria-hidden={!personalizando}
                    className="rv-bp"
                    style={{ transform: personalizando ? 'none' : 'translateY(100%)', visibility: personalizando ? 'visible' : 'hidden', transitionDelay: personalizando ? '0s' : '0s, 440ms' }}
                >
                    <div className="rv-bp__cab">
                        <span className="rv-bp__ceja-roja">Personalizar menú</span>
                        <button
                            type="button"
                            className="rv-mn-b"
                            aria-label="Cerrar personalizar menú"
                            onClick={() => {
                                setPersonalizando(false);
                            }}
                        >
                            <Icono nombre="x" />
                        </button>
                    </div>
                    <PersonalizarBarra
                        menu={v.menu}
                        p={v.personalizar}
                        abierto={personalizando}
                        actual={actual}
                        onListo={() => {
                            setPersonalizando(false);
                        }}
                    />
                </div>
            )}
            {!mas && pop === null && <Privacidad abajo={verBarras ? 'calc(92px + env(safe-area-inset-bottom))' : 'calc(16px + env(safe-area-inset-bottom))'} />}
        </div>
    );
}
